import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Plus, Trash2, Download, PenLine, CheckCircle2, ListChecks, FileText, Loader2, ImagePlus, Ban } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { Card, Button, Input, Select, Textarea, Pill, Spinner } from "@/components/ui";
import { VEHICLE_PHOTO_TAGS, photoTagLabel } from "@/features/hse/vehicleAccidentShared";

// =============================================================================
// IncidentReportPanel — the chain of events and the formal report (mig 418)
// =============================================================================
// James, 3 Oct 2026: "incidents like this [need] the formal report, i.e. from
// the manager not the field … the client needs a chain of events even for
// the day. I made one in an email for this one and it wasn't great, could have
// been set out better with a timeline."
//
// Three pieces that sit on the incident detail page (IncidentRegisterScreen):
//   <ChainOfEvents>    LEFT — timed entries (what, who, source). Type them, or
//                      PULL THE DAY'S RECORD: the start card and its sign-ons,
//                      SWMS acceptances and diary clock-in/out for that project
//                      on that day come up as suggestions; tick the ones that
//                      belong and they become entries.
//   <InvestigationCard> LEFT — what was examined, root cause, contributing
//                      factors, lessons learned, client reference / notified.
//   <ReportEvidence>   LEFT — evidence a manager adds AFTER the event (5 Oct,
//                      James on the Tamborine one: the field photo carries the
//                      capture-time watermark, which is right for a photo
//                      taken on the day but wrong for an incident entered
//                      after the fact). Each file carries "taken on" and a
//                      caption; the PDF prints them with that caption, no
//                      watermark. Files rows, parent incidents, R2 under
//                      incidents/{id}/evidence/ (manager+ via /sign-upload).
//   <FormalReportCard> RAIL — Download the PDF (incidentReportPdf.ts, lazy),
//                      the sign-off state, and the director's Sign off button
//                      (app.incident_sign_off). Any edit after sign-off puts
//                      the report back to DRAFT (database trigger) — the PDF
//                      header says which it is.
// Writes: manager+ or the investigator (same rule as the incident itself).
//
// 1 Nov 2026 (mig 482, Larry's vehicle accident): <ReportEvidence> takes
// several files at once, and on a vehicle accident each carries a tag for
// what it shows (our vehicle / other vehicle / licence-rego-insurance / scene)
// in files.metadata.tag — James: "I have photos of the hilux the bike and the
// other driver's license and details." The broker report groups by the tag;
// licence/ID photos never print on a client-facing report.
//
// 8 Oct 2026 (Ops brief 6, gap 8): a chain entry can be EDITED in place and
// Remove confirms on the row (no browser confirm box). "Pull the day's record"
// no longer offers a diary on-site time that falls after the diary's own
// off-site time as fact — it arrives un-ticked with a "check the time" note
// (a Clock-in tapped when the diary was filled in that evening is the
// instant of the tap, not when the crew arrived).
// =============================================================================

export type TimelineRow = { entry_id: string; happened_at: string; entry: string; who: string | null; source: string; recorded_by: string | null };
const SOURCES: Array<[string, string]> = [["manager", "Manager"], ["field_report", "Field report"], ["start_card", "Start card"], ["sign_on", "Sign-on"], ["swms", "SWMS"], ["diary", "Site diary"], ["client", "Client"], ["other", "Other"]];
const srcLabel = (s: string) => SOURCES.find(x => x[0] === s)?.[1] ?? s;
const fmtT = (d: string) => new Date(d).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
const fmtD = (d?: string | null) => d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDT = (d?: string | null) => d ? new Date(d).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
// local day key (never toISOString — AEST shifts the day)
const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const toLocalInput = (iso: string) => { const d = new Date(iso); return `${dayKey(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

export function useIncidentTimeline(incidentId: string) {
  return useQuery({
    queryKey: ["incident_timeline", incidentId],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("incident_timeline").select("*").eq("incident_id", incidentId).order("happened_at");
      if (error) throw error;
      return (data ?? []) as TimelineRow[];
    },
  });
}

// -----------------------------------------------------------------------------
export function ChainOfEvents({ incidentId, projectId, occurredAt, canWork }: { incidentId: string; projectId: string | null; occurredAt: string; canWork: boolean }) {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useIncidentTimeline(incidentId);
  const [adding, setAdding] = useState(false);
  const [when, setWhen] = useState(toLocalInput(occurredAt));
  const [what, setWhat] = useState("");
  const [who, setWho] = useState("");
  const [source, setSource] = useState("manager");
  const [err, setErr] = useState<string | null>(null);
  const [pulling, setPulling] = useState(false);
  const [suggested, setSuggested] = useState<Array<{ key: string; happened_at: string; entry: string; who: string | null; source: string; on: boolean }> | null>(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["incident_timeline", incidentId] }); qc.invalidateQueries({ queryKey: ["incident_register"] }); };

  async function add() {
    if (!what.trim() || !when) return;
    setErr(null);
    const { error } = await (supabase as any).from("incident_timeline").insert({ incident_id: incidentId, happened_at: new Date(when).toISOString(), entry: what.trim(), who: who.trim() || null, source });
    if (error) { setErr(error.message); return; }
    setWhat(""); setWho(""); setAdding(false); refresh();
  }
  // 8 Oct 2026 (Ops brief 6, gap 8) — an entry can be CORRECTED in place (the
  // time, the words, who, the source), and Remove asks on the row itself, not
  // through the browser's own confirm box. Before this an entry could only be
  // deleted and typed again.
  const [edit, setEdit] = useState<{ id: string; when: string; what: string; who: string; source: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  async function saveEdit() {
    if (!edit || !edit.what.trim() || !edit.when) return;
    setErr(null);
    const { error } = await (supabase as any).from("incident_timeline")
      .update({ happened_at: new Date(edit.when).toISOString(), entry: edit.what.trim(), who: edit.who.trim() || null, source: edit.source })
      .eq("entry_id", edit.id);
    if (error) { setErr(error.message); return; }
    setEdit(null); refresh();
  }
  async function remove(id: string) {
    setErr(null);
    const { error } = await (supabase as any).from("incident_timeline").delete().eq("entry_id", id);
    if (error) { setErr(error.message); return; }
    setConfirmRemove(null); refresh();
  }

  // The day's record — everything Ops already knows about that project on that day.
  async function pullDay() {
    if (!projectId) { setErr("This incident isn't on a project, so there is no day record to pull."); return; }
    setPulling(true); setErr(null);
    try {
      const d = new Date(occurredAt); const day = dayKey(d);
      const from = new Date(d); from.setHours(0, 0, 0, 0); const to = new Date(d); to.setHours(23, 59, 59, 999);
      const out: typeof suggested = [];
      const have = new Set(rows.map(r => `${r.happened_at}|${r.entry}`));
      const push = (happened_at: string, entry: string, who: string | null, source: string, tick = true) => {
        if (!happened_at) return;
        const key = `${happened_at}|${entry}`;
        out!.push({ key, happened_at, entry, who, source, on: tick && !have.has(key) });
      };
      const { data: cards } = await (supabase as any).from("hse_start_cards")
        .select("card_id, card_number, work_area, submitted_at, created_at, supervisor:employees!hse_start_cards_supervisor_employee_id_fkey(first_name, last_name), signons:hse_start_card_signons(name, company, signed_at)")
        .eq("project_id", projectId).eq("card_date", day);
      for (const c of (cards ?? []) as any[]) {
        const sup = c.supervisor ? `${c.supervisor.first_name ?? ""} ${c.supervisor.last_name ?? ""}`.trim() : null;
        push(c.submitted_at ?? c.created_at, `Start card ${c.card_number ?? ""} completed${c.work_area ? ` — ${c.work_area}` : ""}`, sup, "start_card");
        for (const s of (c.signons ?? []) as any[]) push(s.signed_at, `Signed on to the start card`, `${s.name ?? ""}${s.company ? ` (${s.company})` : ""}`.trim() || null, "sign_on");
      }
      const { data: accs } = await (supabase as any).from("swms_acceptances").select("accepted_at, signer_name_raw, signer_company").eq("project_id", projectId).gte("accepted_at", from.toISOString()).lte("accepted_at", to.toISOString());
      for (const a of (accs ?? []) as any[]) push(a.accepted_at, "SWMS accepted", `${a.signer_name_raw ?? ""}${a.signer_company ? ` (${a.signer_company})` : ""}`.trim() || null, "swms");
      const { data: diary } = await (supabase as any).from("site_diary_entries").select("clocked_in_at, clocked_out_at, started_at, ended_at, scope_done, delays, who_on_site").eq("project_id", projectId).eq("entry_date", day);
      for (const e of (diary ?? []) as any[]) {
        const crew = Array.isArray(e.who_on_site) ? e.who_on_site.map((p: any) => typeof p === "string" ? p : p?.name).filter(Boolean).join(", ") : null;
        // 8 Oct 2026 (gap 8, James 6 Oct on INC-26-0003: "07:00 pm Crew on site"
        // listed after "03:30 pm Crew off site"). The diary stores a TAPPED
        // "Clock in" as the instant of the tap, and a typed time as that time on
        // the entry's date. A crew that fills the diary in that evening and taps
        // Clock in then gets an on-site time later than the off-site time they
        // typed. So: a tapped time that falls AFTER the day's off-site time is
        // not offered as fact — it comes un-ticked and says so, for the manager
        // to correct (entries are editable now).
        const onAt = e.clocked_in_at ?? e.started_at;
        const offAt = e.clocked_out_at ?? e.ended_at;
        const onLooksWrong = !!(onAt && offAt && new Date(onAt).getTime() > new Date(offAt).getTime());
        if (onAt) push(onAt, onLooksWrong ? "Crew on site (site diary) — CHECK THE TIME: the diary's on-site time is after its off-site time, it was probably tapped when the diary was filled in" : "Crew on site (site diary)", crew, "diary", !onLooksWrong);
        if (offAt) push(offAt, `Crew off site (site diary)${e.scope_done ? ` — ${String(e.scope_done).slice(0, 120)}` : ""}`, crew, "diary");
        if (e.delays) push(e.clocked_out_at ?? e.ended_at ?? e.clocked_in_at ?? e.started_at, `Diary delays: ${String(e.delays).slice(0, 160)}`, crew, "diary");
      }
      out!.sort((a, b) => new Date(a.happened_at).getTime() - new Date(b.happened_at).getTime());
      setSuggested(out);
    } catch (e: any) { setErr(e?.message ?? String(e)); }
    finally { setPulling(false); }
  }
  async function acceptSuggested() {
    const picked = (suggested ?? []).filter(s => s.on);
    if (!picked.length) { setSuggested(null); return; }
    const { error } = await (supabase as any).from("incident_timeline").insert(picked.map(s => ({ incident_id: incidentId, happened_at: s.happened_at, entry: s.entry, who: s.who, source: s.source })));
    if (error) { setErr(error.message); return; }
    setSuggested(null); refresh();
  }

  const grouped = useMemo(() => {
    const m = new Map<string, TimelineRow[]>();
    for (const r of rows) { const k = fmtD(r.happened_at); m.set(k, [...(m.get(k) ?? []), r]); }
    return [...m.entries()];
  }, [rows]);

  return (
    <Card>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5"><Clock size={12}/> Chain of events</div>
          {canWork && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" icon={ListChecks} disabled={pulling} onClick={pullDay}>{pulling ? <Spinner size={12}/> : "Pull the day's record"}</Button>
              <Button size="sm" icon={Plus} onClick={() => setAdding(v => !v)}>Add entry</Button>
            </div>
          )}
        </div>
        {err && <div className="text-[12px] text-lost">{err}</div>}

        {suggested && (
          <div className="rounded-lg border border-accent/40 bg-accent/5 p-3 space-y-2">
            <div className="text-[12px] text-text">
              {suggested.length === 0 ? "Nothing on record for that project on that day — no start card, sign-ons, SWMS acceptances or diary." : `From Ops' own record of that day — tick what belongs on the timeline:`}
            </div>
            {suggested.map((s, i) => (
              <label key={s.key} className={`flex items-start gap-2 text-[12px] ${!s.on && rows.some(r => `${r.happened_at}|${r.entry}` === s.key) ? "opacity-50" : ""}`}>
                <input type="checkbox" checked={s.on} onChange={e => setSuggested(x => x!.map((y, j) => j === i ? { ...y, on: e.target.checked } : y))} className="mt-0.5"/>
                <span><span className="font-mono text-accent">{fmtT(s.happened_at)}</span> {s.entry}{s.who ? <span className="text-text-muted"> — {s.who}</span> : null} <span className="text-[10px] text-text-dim">{srcLabel(s.source)}</span></span>
              </label>
            ))}
            <div className="flex gap-2 pt-1">
              {suggested.length > 0 && <Button size="sm" onClick={acceptSuggested}>Add ticked entries</Button>}
              <Button size="sm" variant="ghost" onClick={() => setSuggested(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {adding && (
          <div className="rounded-lg border border-line p-3 grid sm:grid-cols-[170px_1fr] gap-2">
            <Input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}/>
            <Input value={what} onChange={e => setWhat(e.target.value)} placeholder="What happened — one line"/>
            <Input value={who} onChange={e => setWho(e.target.value)} placeholder="Who (optional)"/>
            <div className="flex gap-2">
              <Select value={source} onChange={e => setSource(e.target.value)}>{SOURCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
              <Button size="sm" onClick={add} disabled={!what.trim()}>Add</Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {isLoading ? <div className="py-4 flex justify-center"><Spinner size={16}/></div>
          : rows.length === 0 ? <div className="text-[12px] text-text-muted">No entries yet. The report always carries the occurred and reported times; add what happened before and after — or pull the day's record.</div>
          : (
            <div className="space-y-3">
              {grouped.map(([day, list]) => (
                <div key={day}>
                  <div className="text-[11px] font-medium text-text-muted mb-1">{day}</div>
                  <ol className="relative border-l border-line ml-2 space-y-2">
                    {list.map(r => edit?.id === r.entry_id ? (
                      <li key={r.entry_id} className="pl-4 relative">
                        <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-accent"/>
                        <div className="rounded-lg border border-accent/40 bg-accent/5 p-2 grid sm:grid-cols-[170px_1fr] gap-2">
                          <Input type="datetime-local" value={edit.when} onChange={e => setEdit({ ...edit, when: e.target.value })}/>
                          <Input value={edit.what} onChange={e => setEdit({ ...edit, what: e.target.value })} placeholder="What happened — one line"/>
                          <Input value={edit.who} onChange={e => setEdit({ ...edit, who: e.target.value })} placeholder="Who (optional)"/>
                          <div className="flex gap-2">
                            <Select value={edit.source} onChange={e => setEdit({ ...edit, source: e.target.value })}>{SOURCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
                            <Button size="sm" onClick={saveEdit} disabled={!edit.what.trim() || !edit.when}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
                          </div>
                        </div>
                      </li>
                    ) : (
                      <li key={r.entry_id} className="pl-4 relative">
                        <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-accent"/>
                        <div className="flex items-start gap-2">
                          <span className="font-mono text-[12px] text-accent shrink-0 w-[68px] whitespace-nowrap">{fmtT(r.happened_at)}</span>
                          <span className="flex-1 min-w-0 text-[13px] text-text">
                            {r.entry}
                            {r.who && <span className="text-text-muted"> — {r.who}</span>}
                            <span className="ml-2 text-[10px] text-text-dim uppercase tracking-wider">{srcLabel(r.source)}</span>
                          </span>
                          {canWork && confirmRemove !== r.entry_id && (
                            <>
                              <button type="button" onClick={() => { setConfirmRemove(null); setEdit({ id: r.entry_id, when: toLocalInput(r.happened_at), what: r.entry, who: r.who ?? "", source: r.source }); }} className="text-[11px] text-accent hover:underline shrink-0">Edit</button>
                              <button type="button" onClick={() => setConfirmRemove(r.entry_id)} className="text-text-dim hover:text-danger shrink-0" title="Remove"><Trash2 size={13}/></button>
                            </>
                          )}
                          {canWork && confirmRemove === r.entry_id && (
                            <span className="text-[11px] shrink-0 inline-flex items-center gap-2">
                              <span className="text-danger">Remove this entry?</span>
                              <button type="button" onClick={() => remove(r.entry_id)} className="text-danger font-medium hover:underline">Yes, remove</button>
                              <button type="button" onClick={() => setConfirmRemove(null)} className="text-text-muted hover:underline">No</button>
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
export function InvestigationCard({ incident, canWork, onPatch }: {
  incident: { investigation_summary?: string | null; root_cause?: string | null; contributing_factors?: any; lessons_learned?: string | null; client_reference?: string | null; client_notified_at?: string | null; status: string };
  canWork: boolean;
  onPatch: (fields: Record<string, any>) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState(incident.investigation_summary ?? "");
  const [root, setRoot] = useState(incident.root_cause ?? "");
  const [factors, setFactors] = useState<string>(Array.isArray(incident.contributing_factors) ? incident.contributing_factors.join("\n") : "");
  const [lessons, setLessons] = useState(incident.lessons_learned ?? "");
  const [clientRef, setClientRef] = useState(incident.client_reference ?? "");
  const [clientNotified, setClientNotified] = useState(incident.client_notified_at ? toLocalInput(incident.client_notified_at) : "");
  const [busy, setBusy] = useState(false);
  const factorList: string[] = Array.isArray(incident.contributing_factors) ? incident.contributing_factors : [];

  async function save() {
    setBusy(true);
    const ok = await onPatch({
      investigation_summary: summary.trim() || null, root_cause: root.trim() || null,
      contributing_factors: factors.split("\n").map(s => s.trim()).filter(Boolean),
      lessons_learned: lessons.trim() || null, client_reference: clientRef.trim() || null,
      client_notified_at: clientNotified ? new Date(clientNotified).toISOString() : null,
    });
    setBusy(false); if (ok) setEditing(false);
  }
  const empty = !incident.investigation_summary && !incident.root_cause && factorList.length === 0 && !incident.lessons_learned;

  return (
    <Card>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5"><FileText size={12}/> Investigation and findings</div>
          {canWork && !editing && <Button size="sm" variant="secondary" icon={PenLine} onClick={() => setEditing(true)}>{empty ? "Write it up" : "Edit"}</Button>}
        </div>
        {editing ? (
          <div className="space-y-2">
            <label className="text-[11px] text-text-muted block">What was examined<Textarea rows={3} value={summary} onChange={e => setSummary(e.target.value)} placeholder="Who was spoken to, what was inspected, what records were checked…"/></label>
            <label className="text-[11px] text-text-muted block">Root cause<Textarea rows={2} value={root} onChange={e => setRoot(e.target.value)} placeholder="The one finding that, had it been different, would have prevented it."/></label>
            <label className="text-[11px] text-text-muted block">Contributing factors — one per line<Textarea rows={3} value={factors} onChange={e => setFactors(e.target.value)}/></label>
            <label className="text-[11px] text-text-muted block">Lessons learned<Textarea rows={2} value={lessons} onChange={e => setLessons(e.target.value)}/></label>
            <div className="grid sm:grid-cols-2 gap-2">
              <label className="text-[11px] text-text-muted block">Client reference<Input value={clientRef} onChange={e => setClientRef(e.target.value)} placeholder="Their incident / ticket number"/></label>
              <label className="text-[11px] text-text-muted block">Client notified<Input type="datetime-local" value={clientNotified} onChange={e => setClientNotified(e.target.value)}/></label>
            </div>
            <div className="flex gap-2"><Button size="sm" onClick={save} disabled={busy}>{busy ? <Spinner size={12}/> : "Save"}</Button><Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button></div>
            <div className="text-[11px] text-text-muted">Saving after a director has signed the report off puts it back to DRAFT — they sign the new revision.</div>
          </div>
        ) : empty ? (
          <div className="text-[12px] text-text-muted">Not written up yet. The report prints "Investigation not yet written up" until it is.</div>
        ) : (
          <div className="space-y-2 text-[13px]">
            {incident.investigation_summary && <div><div className="text-[10px] uppercase tracking-wider text-text-muted">What was examined</div><div className="whitespace-pre-wrap">{incident.investigation_summary}</div></div>}
            {incident.root_cause && <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Root cause</div><div className="whitespace-pre-wrap">{incident.root_cause}</div></div>}
            {factorList.length > 0 && <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Contributing factors</div><ul className="list-disc pl-5">{factorList.map((f, i) => <li key={i}>{f}</li>)}</ul></div>}
            {incident.lessons_learned && <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Lessons learned</div><div className="whitespace-pre-wrap">{incident.lessons_learned}</div></div>}
            {(incident.client_reference || incident.client_notified_at) && <div className="text-[12px] text-text-muted">Client{incident.client_reference ? ` ref ${incident.client_reference}` : ""}{incident.client_notified_at ? ` · notified ${fmtDT(incident.client_notified_at)}` : ""}</div>}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
export function FormalReportCard({ incidentId, signedOffAt, signedOffBy, reportRevision, isDirector, onDownload, onSigned, factorCount = 0, actionCount = 0, vehicle = false, hasMap = false, hasFindings = false }: {
  incidentId: string; signedOffAt: string | null; signedOffBy: string | null; reportRevision: number;
  isDirector: boolean; onDownload: () => Promise<void>; onSigned: () => void;
  // 8 Oct 2026 (Ops brief 6, gap 10) — a nudge, never a block: a report with
  // fewer corrective actions than contributing factors usually has a factor
  // nobody has answered.
  factorCount?: number; actionCount?: number;
  // 2 Nov 2026 (mig 489) — what the database will insist on before it signs:
  // a vehicle accident needs its map; anything else needs the findings. Said
  // up front, so "press OK and it's still Draft" (James, INC-26-0004) cannot
  // happen again without a reason on the screen.
  vehicle?: boolean; hasMap?: boolean; hasFindings?: boolean;
}) {
  const shortBy = factorCount > actionCount ? factorCount - actionCount : 0;
  const nudge = shortBy > 0
    ? `${factorCount} contributing factor${factorCount === 1 ? "" : "s"} but ${actionCount === 0 ? "no corrective actions" : `only ${actionCount} corrective action${actionCount === 1 ? "" : "s"}`} — does each factor have something that stops it happening again?`
    : null;
  const [busy, setBusy] = useState<"dl" | "sign" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);   // 2 Nov 2026 — the app's own confirm, not the browser's box
  const blocker = vehicle ? (hasMap ? null : "the accident map — draw it on the Vehicle accident card") : (hasFindings ? null : "the investigation findings (what was examined / root cause)");
  const { data: signer } = useQuery({
    queryKey: ["user_name", signedOffBy],
    enabled: !!signedOffBy,
    queryFn: async () => {
      const { data } = await (supabase as any).from("users").select("employee:employees(first_name, last_name)").eq("user_id", signedOffBy).maybeSingle();
      return data?.employee ? `${data.employee.first_name ?? ""} ${data.employee.last_name ?? ""}`.trim() : null;
    },
  });
  async function download() { setBusy("dl"); setErr(null); try { await onDownload(); } catch (e: any) { setErr(e?.message ?? String(e)); } finally { setBusy(null); } }
  async function sign() {
    setBusy("sign"); setErr(null); setConfirming(false);
    const { error } = await (supabase as any).schema("app").rpc("incident_sign_off", { p_incident_id: incidentId });
    if (error) setErr(error.message.replace(/^.*?: /, "")); else onSigned();
    setBusy(null);
  }
  return (
    <Card>
      <div className="p-4 space-y-2.5">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">Formal incident report</div>
        {nudge && !signedOffAt && <div className="text-[12px] text-warn border border-warn/30 bg-warn/10 rounded px-2.5 py-1.5">{nudge}</div>}
        {signedOffAt
          ? <div className="text-[13px] flex items-start gap-2"><CheckCircle2 size={16} className="text-ok mt-0.5 shrink-0"/><span><span className="font-medium">Rev {reportRevision} signed off</span><div className="text-[12px] text-text-muted">{signer ?? "Director"} · {fmtDT(signedOffAt)}</div></span></div>
          : <div className="text-[13px] flex items-start gap-2"><Pill label="draft" tone="warning"/><span className="text-[12px] text-text-muted">Prints DRAFT until a director signs it off{reportRevision > 0 ? ` (last signed Rev ${reportRevision})` : ""}.</span></div>}
        {err && <div className="text-[12px] text-danger border border-danger/30 bg-danger/10 rounded px-2.5 py-1.5"><b>Not signed.</b> {err}</div>}
        {isDirector && !signedOffAt && blocker && <div className="text-[12px] text-text-muted">Sign-off needs {blocker}.</div>}
        {confirming ? (
          <div className="rounded-md border border-accent/40 bg-accent/5 p-3 space-y-2">
            <div className="text-[13px] font-medium">Sign this report off as a director?</div>
            {nudge && <div className="text-[12px] text-warn">Before you sign: {nudge}</div>}
            <div className="text-[12px] text-text-muted">The PDF prints your name and the date as Rev {reportRevision + 1}. Any later edit to the facts, the drawings or the chain of events puts it back to draft.</div>
            <div className="flex gap-2"><Button size="sm" icon={busy === "sign" ? Loader2 : PenLine} disabled={busy !== null} onClick={sign}>Yes, sign it off</Button><Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setConfirming(false)}>Not yet</Button></div>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" icon={busy === "dl" ? Loader2 : Download} disabled={busy !== null} onClick={download}>Download PDF</Button>
            {isDirector && !signedOffAt && <Button size="sm" variant="secondary" icon={PenLine} disabled={busy !== null || !!blocker} title={blocker ? `Needs ${blocker}` : undefined} onClick={() => setConfirming(true)}>Sign off</Button>}
          </div>
        )}
        <div className="text-[11px] text-text-muted">Facts · people · chain of events · immediate actions · investigation · corrective actions · notifications · evidence · sign-off. Email it from Outlook.</div>
      </div>
    </Card>
  );
}


// -----------------------------------------------------------------------------
export type ReportEvidenceFile = { file_id: string; file_name: string | null; content_type: string | null; change_summary: string | null; version_label: string | null; metadata?: { tag?: string | null } | null };

export function useReportEvidence(incidentId: string) {
  return useQuery({
    queryKey: ["incident_report_evidence", incidentId],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("files").select("file_id, file_name, content_type, change_summary, version_label, metadata")
        .eq("parent_table", "incidents").eq("parent_id", incidentId).order("created_at");
      if (error) throw error;
      return (data ?? []) as ReportEvidenceFile[];
    },
  });
}

/** Fetch an evidence image through the gateway and hand back a data URL for pdfmake. */
export async function reportEvidenceDataUrl(fileId: string, maxPx = 900): Promise<string | null> {
  try {
    const r = await apiFetch(`/files/${encodeURIComponent(fileId)}/bytes`);
    if (!r.ok) return null;
    const blob = await r.blob();
    if (!blob.type.startsWith("image/")) return null;
    const bmp = await createImageBitmap(blob);
    const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
    const cv = document.createElement("canvas");
    cv.width = Math.round(bmp.width * scale); cv.height = Math.round(bmp.height * scale);
    cv.getContext("2d")!.drawImage(bmp, 0, 0, cv.width, cv.height);
    return cv.toDataURL("image/jpeg", 0.8);
  } catch { return null; }
}

export function ReportEvidence({ incidentId, occurredAt, canWork, vehicle = false }: { incidentId: string; occurredAt: string; canWork: boolean; vehicle?: boolean }) {
  const qc = useQueryClient();
  const { data: files = [] } = useReportEvidence(incidentId);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<File[]>([]);
  const [caption, setCaption] = useState("");
  const [tag, setTag] = useState("coretel_vehicle");
  const [takenOn, setTakenOn] = useState(dayKey(new Date(occurredAt)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  async function thumb(f: ReportEvidenceFile) {
    if (thumbs[f.file_id] || !(f.content_type ?? "").startsWith("image/")) return;
    const d = await reportEvidenceDataUrl(f.file_id, 300);
    if (d) setThumbs(t => ({ ...t, [f.file_id]: d }));
  }
  files.forEach(f => { void thumb(f); });

  async function upload() {
    if (picked.length === 0) return;
    setBusy(true); setErr(null);
    try {
      // several at once (1 Nov 2026) — each is its own files row with the same caption, date and tag
      for (const file of picked) {
        const key = `incidents/${incidentId}/evidence/${Date.now().toString(36)}_${file.name.replace(/[^\w.\-]+/g, "_")}`;
        const signRes = await apiFetch("/sign-upload", { method: "POST", body: JSON.stringify({ bucket: "coretel-prod", object_key: key, content_type: file.type || "application/octet-stream", expires_in_seconds: 900 }) });
        if (!signRes.ok) throw new Error(`sign_failed_${signRes.status}`);
        const { upload_url } = await signRes.json();
        const put = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
        if (!put.ok) throw new Error(`upload_failed_${put.status}`);
        const { error } = await supabase.from("files").insert({
          parent_table: "incidents", parent_id: incidentId, r2_bucket: "coretel-prod", r2_object_key: key,
          file_name: file.name, content_type: file.type || "application/octet-stream", file_size_bytes: file.size,
          change_summary: caption.trim() || null, version_label: takenOn || null,   // caption · taken-on date
          metadata: vehicle ? { tag } : null,                                        // mig 482 — what the photo shows
          security_class: vehicle && tag === "details" ? "commercial" : "internal",  // a licence / ID photo: manager+ only (files_read), for the insurer
        } as any);
        if (error) throw error;
      }
      setPicked([]); setCaption(""); setAdding(false);
      qc.invalidateQueries({ queryKey: ["incident_report_evidence", incidentId] });
    } catch (e: any) { setErr(e?.message ?? String(e)); } finally { setBusy(false); }
  }
  async function remove(f: ReportEvidenceFile) {
    if (!window.confirm(`Remove ${f.file_name ?? "this file"} from the report?`)) return;
    const { error } = await (supabase as any).from("files").delete().eq("file_id", f.file_id);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["incident_report_evidence", incidentId] });
  }

  return (
    <Card>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted flex items-center gap-1.5"><ImagePlus size={12}/> Report evidence — added after the event</div>
          {canWork && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(v => !v)}>Add a photo or drawing</Button>}
        </div>
        <div className="text-[11px] text-text-muted">Photos the crew takes on the phone carry the capture-time watermark — keep using those on the day. This is for what comes later: a site map, a drawing, a photo supplied by the client, or an incident entered after it happened. Each prints in the report with its caption and the date it was taken.</div>
        {err && <div className="text-[12px] text-lost">{err}</div>}
        {adding && (
          <div className="rounded-lg border border-line p-3 space-y-2">
            <label className="flex items-center gap-2 rounded-md border border-dashed border-line px-3 py-2 text-[13px] cursor-pointer hover:border-accent/50">
              <ImagePlus size={14} className="text-accent"/><span className="flex-1 truncate">{picked.length === 1 ? picked[0].name : picked.length > 1 ? `${picked.length} images` : "Choose images (JPG/PNG) — several at once is fine…"}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={e => setPicked(Array.from(e.target.files ?? []))}/>
            </label>
            {vehicle && (
              <div>
                <div className="text-[11px] text-text-muted mb-1">What these show</div>
                <div className="flex flex-wrap gap-1.5">
                  {VEHICLE_PHOTO_TAGS.map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setTag(k)}
                      className={`text-[11px] px-2.5 py-1.5 rounded border transition ${tag === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>{l}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-[160px_1fr] gap-2">
              <label className="text-[11px] text-text-muted">Taken on<Input type="date" value={takenOn} onChange={e => setTakenOn(e.target.value)}/></label>
              <label className="text-[11px] text-text-muted">Caption<Input value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Site map — drop zone and work positions (J. O'Sullivan)"/></label>
            </div>
            <div className="flex gap-2"><Button size="sm" onClick={upload} disabled={picked.length === 0 || busy}>{busy ? <Spinner size={12}/> : picked.length > 1 ? `Add ${picked.length} to the report` : "Add to the report"}</Button><Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button></div>
          </div>
        )}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {files.map(f => (
              <div key={f.file_id} className="w-40">
                {thumbs[f.file_id] ? <img src={thumbs[f.file_id]} alt="" className="w-40 h-28 object-cover rounded-md border border-line"/> : <div className="w-40 h-28 rounded-md border border-line bg-ink flex items-center justify-center text-[11px] text-text-muted">{f.file_name}</div>}
                <div className="text-[11px] text-text mt-1 leading-tight">{f.change_summary ?? f.file_name}</div>
                {photoTagLabel(f.metadata?.tag) && <div className="text-[10px] text-accent">{photoTagLabel(f.metadata?.tag)}</div>}
                <div className="text-[10px] text-text-muted flex items-center justify-between">{f.version_label ? `taken ${fmtD(f.version_label)}` : ""}{canWork && <button type="button" onClick={() => remove(f)} className="hover:text-danger"><Trash2 size={11}/></button>}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Void — a test entry or a duplicate. Status rejected_duplicate (already in the
// register's vocabulary) keeps the row for the audit trail and takes it off
// the open list. James, 5 Oct: "delete INC-002 or archive it as a test".
export function VoidIncidentButton({ incidentId, canManage, onDone }: { incidentId: string; canManage: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  if (!canManage) return null;
  async function run() {
    const why = window.prompt("Void this incident as a test or duplicate? It stays on the record as 'rejected / duplicate' and leaves the open list. Say why:", "Test entry — superseded by a properly entered report");
    if (why == null) return;
    setBusy(true);
    const { error } = await (supabase as any).from("incidents").update({ status: "rejected_duplicate", closed_at: new Date().toISOString(), root_cause: why.trim() || null }).eq("incident_id", incidentId);
    setBusy(false);
    if (error) window.alert(error.message); else onDone();
  }
  return <Button size="sm" variant="ghost" icon={Ban} disabled={busy} onClick={run}>Void — test or duplicate</Button>;
}
